/**
 * Client-side mirror of the server's gate rules
 * (apr-backend src/utils/sign/validate.ts).
 *
 * Drives the stepper's reachability, the footer hint, the palette checklist and
 * the primary button's disabled state — live, before the server is asked. The
 * server remains the source of truth: it re-runs these rules against a fresh
 * board-column fetch, and its verdict wins.
 *
 * Two gates, mirroring the backend's split. An ENVELOPE must be sendable (real
 * people, a real destination); a TEMPLATE only has to be a usable starting
 * point, so it needs neither.
 *
 * Pure functions only (no monday, no fetch) so the whole module is testable.
 */

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** Fields sitting on a document that is no longer in the list. */
function orphanedFieldCount(subject) {
  const known = new Set((subject.documents || []).map((d) => d.id));
  return [...(subject.prefillFields || []), ...(subject.signerFields || [])].filter(
    (f) => !known.has(f.documentId),
  ).length;
}

/** Prefill fields whose board column has since been deleted. */
function deletedColumnCount(subject, board) {
  if (!board) return 0;
  const known = new Set((board.prefillColumns || []).map((c) => c.id));
  return (subject.prefillFields || []).filter((f) => !known.has(f.columnId)).length;
}

/**
 * Board-column mappings on a set of signer fields — the client mirror of the
 * backend's `collectMappingIssues`.
 *
 * Every issue is step 2: the properties panel is where a mapping is chosen, so
 * that is where it has to be fixed. A missing `board` skips the existence check
 * and still runs the collision one, which needs nothing external.
 */
function mappingIssues(signerFields, board) {
  const errors = [];
  const mapped = (signerFields || []).filter((f) => f.mappingColumnId && f.mappingColumnType);
  if (!mapped.length) return errors;

  const byId = new Map((board?.mappingColumns || []).map((c) => [c.id, c.type]));
  const claimedBy = new Map();

  for (const field of mapped) {
    const name = field.label || String(field.type).replace('_', ' ');

    if (board) {
      const liveType = byId.get(field.mappingColumnId);
      if (!liveType) {
        errors.push({
          code: 'mapping_column_missing',
          step: 2,
          message: `“${name}” saves to a board column that no longer exists.`,
        });
        continue;
      }
      if (liveType !== field.mappingColumnType) {
        errors.push({
          code: 'mapping_column_type_changed',
          step: 2,
          message: `“${name}” saves to a column that is no longer a ${field.mappingColumnType} column.`,
        });
        continue;
      }
    }

    const rival = claimedBy.get(field.mappingColumnId);
    if (rival) {
      // A board column holds one value, so the second write erases the first
      // and nothing downstream can recover which answer was meant.
      errors.push({
        code: 'mapping_column_collision',
        step: 2,
        message: `“${name}” and “${rival}” both save to the same board column.`,
      });
      continue;
    }
    claimedBy.set(field.mappingColumnId, name);
  }

  return errors;
}

/**
 * Can this envelope be sent?
 *
 * Returns { canSend, errors: [{ code, step, message }], warnings } — same codes
 * as the server, so a 422 from the send call can reuse this rendering.
 */
export function validateEnvelope(envelope, board, boardSettings = null) {
  const errors = [];
  const warnings = [];
  if (!envelope) return { canSend: false, errors, warnings };

  const documents = envelope.documents || [];
  const signers = envelope.signers || [];
  const signerFields = envelope.signerFields || [];

  if (!documents.length) {
    errors.push({ code: 'no_documents', step: 1, message: 'Add at least one document.' });
  }

  if (!signers.length) {
    errors.push({ code: 'no_signers', step: 2, message: 'Add at least one signer.' });
  }

  /*
    Signer problems carry WHO they belong to and a short `need` phrase beside
    the full message.

    The checklist groups by person — one line per signer rather than one per
    rule — because "Signer 1 has no signature field" and "Signer 1 has no
    address" are one person, half set up, and putting them under one heading
    turns six alarms back into three people. The `need` phrases are what the
    checklist renders under the name, one line each — reading them from data is
    the alternative to parsing the messages back apart, which would break the
    first time a message was reworded.

    The `message` stays complete and standalone: the footer hint and any caller
    that shows one error on its own still needs a sentence that reads alone.
  */
  signers.forEach((signer, i) => {
    const who = { signerIndex: i, roleLabel: signer.roleLabel };
    if (!signerFields.some((f) => f.signerIndex === i && f.type === 'signature')) {
      errors.push({
        code: 'signer_missing_signature',
        step: 2,
        ...who,
        need: 'a signature field',
        message: `Please add ${signer.roleLabel} signature field`,
      });
    }
    const email = (signer.email || '').trim();
    if (!email) {
      errors.push({
        code: 'signer_missing_email',
        step: 2,
        ...who,
        need: 'an email address',
        message: `${signer.roleLabel} has no email address`,
      });
    } else if (!EMAIL_RE.test(email)) {
      errors.push({
        code: 'signer_email_invalid',
        step: 2,
        ...who,
        need: 'a valid email address',
        message: `“${email}” is not a valid address for ${signer.roleLabel}.`,
      });
    }
  });

  /*
    Where the signed PDFs land is BOARD policy now, not this request's — so
    these two carry `scope: 'board'` instead of a step number. Nothing in the
    wizard can fix them, which is exactly why they must not be reported as a
    step to go back to: `maxReachableStep` ignores them, and the request panel
    renders a link to the Settings tab when it sees one.

    `boardSettings` is optional. Without it these checks cannot run, and a
    caller that has not loaded them gets a gate that is quieter than the
    server's rather than one that guesses.
  */
  if (boardSettings) {
    if (!boardSettings.output?.fileColumnId) {
      errors.push({
        code: 'no_output_file_column',
        scope: 'board',
        // Names the fix, not the state. This is the one blocking problem that
        // cannot be solved anywhere in this wizard, so the message has to send
        // the reader somewhere — the checklist puts the settings link directly
        // under it.
        message: 'Add a File column for signed documents in board Settings.',
      });
    } else if (board && !(board.fileColumns || []).some((c) => c.id === boardSettings.output.fileColumnId)) {
      errors.push({
        code: 'output_file_column_invalid',
        scope: 'board',
        message: 'The board’s signed-document column no longer exists.',
      });
    }
  }

  errors.push(...mappingIssues(signerFields, board));

  const orphans = orphanedFieldCount(envelope);
  if (orphans) {
    errors.push({
      code: 'fields_orphan_document',
      step: 2,
      message: `${orphans} field${orphans === 1 ? '' : 's'} sit on a document that was removed.`,
    });
  }

  const deleted = deletedColumnCount(envelope, board);
  if (deleted) {
    errors.push({
      code: 'prefill_columns_deleted',
      step: 2,
      message: `${deleted} placed ${deleted === 1 ? 'field maps' : 'fields map'} to a deleted board column.`,
    });
  }

  // A document nobody signs is legitimate — an exhibit or a schedule attached
  // for reference — so this informs rather than blocks.
  const withFields = new Set(signerFields.map((f) => f.documentId));
  for (const doc of documents) {
    if (!withFields.has(doc.id)) {
      warnings.push({
        code: 'document_without_signature',
        step: 2,
        message: `“${doc.name}” has no signature fields — it will be sent for reading only.`,
      });
    }
  }

  return { canSend: errors.length === 0, errors, warnings };
}

/**
 * Can this template be published?
 *
 * Lighter than the envelope gate on purpose: a template holds ROLES, not
 * people, and sends nothing. All it must promise is that every role it defines
 * has somewhere to sign, on a document that still exists.
 */
export function validateTemplate(template, board) {
  const errors = [];
  const warnings = [];
  if (!template) return { canPublish: false, errors, warnings };

  const documents = template.documents || [];
  const roles = template.roles || [];
  const signerFields = template.signerFields || [];

  if (!documents.length) {
    errors.push({ code: 'no_documents', step: 1, message: 'Add at least one document.' });
  }
  // Step 2, like every other role concern — the palette is the only place a
  // template surfaces them now.
  if (!roles.length) {
    errors.push({ code: 'no_roles', step: 2, message: 'Add at least one signer role.' });
  }

  roles.forEach((role, i) => {
    if (!signerFields.some((f) => f.signerIndex === i && f.type === 'signature')) {
      errors.push({
        code: 'role_missing_signature',
        step: 2,
        message: `Please add ${role.roleLabel} signature field`,
      });
    }
  });

  errors.push(...mappingIssues(signerFields, board));

  const orphans = orphanedFieldCount(template);
  if (orphans) {
    errors.push({
      code: 'fields_orphan_document',
      step: 2,
      message: `${orphans} field${orphans === 1 ? '' : 's'} sit on a document that was removed.`,
    });
  }

  const deleted = deletedColumnCount(template, board);
  if (deleted) {
    errors.push({
      code: 'prefill_columns_deleted',
      step: 2,
      message: `${deleted} placed ${deleted === 1 ? 'field maps' : 'fields map'} to a deleted board column.`,
    });
  }

  return { canPublish: errors.length === 0, errors, warnings };
}

/**
 * Step 1 is always reachable; every step past it needs at least one document.
 *
 * Both surfaces are two steps now — the envelope's third collapsed into step 2
 * when signers moved into the properties panel. The "step 2 must be FINISHED
 * before step 3 opens" rule went with it: there is no later step to protect,
 * and the send button is now the only gate, which is where the checklist in
 * the request panel does its work.
 *
 * `board` and `boardSettings` are accepted and unused, kept so callers do not
 * have to know which of these two functions needs what.
 */
export function maxReachableStep(subject, lastStep = 2) {
  if (!(subject?.documents || []).length) return 1;
  return lastStep;
}

/**
 * The contextual footer hint for the current step.
 *
 * On the LAST step the hint also reports failures from step 1, as
 * `{ prefix: 'Step 1', text }` — that is the class of failure people most often
 * don't notice, and the last step is where they are about to act.
 *
 * BOARD-scoped errors are deliberately excluded from the prefix path: they
 * belong to no step, so "Step undefined" is the only thing that treatment could
 * produce. They surface in the request panel's checklist instead, next to the
 * link that can actually fix them.
 *
 * `mode` is 'envelope' or 'template'. Returns { text, tone: 'warn' | 'ok' } or null.
 */
export function stepHint(subject, board, step, mode = 'envelope', boardSettings = null) {
  const isEnvelope = mode === 'envelope';
  const lastStep = 2;
  const { errors } = isEnvelope
    ? validateEnvelope(subject, board, boardSettings)
    : validateTemplate(subject, board);

  if (step === 1 && !(subject?.documents || []).length) {
    return { text: 'Add a document to continue.', tone: 'warn' };
  }
  if (step === 1 && subject?.needsReview) {
    return { text: 'Field placements were carried over — review them on step 2.', tone: 'warn' };
  }

  const own = errors.find((e) => e.step === step);
  if (step < lastStep) {
    return own ? { text: own.message, tone: 'warn' } : null;
  }

  // `prefix` rather than a sentence: the step number is the actionable half of a
  // cross-step failure, and the footer renders it with its own weight.
  const earlier = errors.find((e) => e.step && e.step < step);
  if (earlier) {
    return { tone: 'warn', prefix: `Step ${earlier.step}`, text: earlier.message };
  }
  if (own) return { text: own.message, tone: 'warn' };

  // Board problems block the send but name no step, so they read as a plain
  // sentence — going "back" is not the fix.
  const boardIssue = errors.find((e) => e.scope === 'board');
  if (boardIssue) return { text: boardIssue.message, tone: 'warn' };

  return {
    text: isEnvelope
      ? 'Everything looks good — you can send this request.'
      : 'Everything looks good — you can publish this template.',
    tone: 'ok',
  };
}

/**
 * Live per-signer status for the palette footer checklist — [{ label, done }]
 * plus an item-field count. Works for both envelopes (signers) and templates
 * (roles), since the palette only cares about labels and signature coverage.
 */
export function signerChecklist(subject) {
  const parties = subject?.signers || subject?.roles || [];
  const signerFields = subject?.signerFields || [];

  return {
    signers: parties.map((s, i) => ({
      label: `${s.roleLabel} signature`,
      done: signerFields.some((f) => f.signerIndex === i && f.type === 'signature'),
    })),
    prefillCount: (subject?.prefillFields || []).length,
  };
}
